import { Line, SessionData, SessionTableRow, Subroutine } from '@/types/analytics';
import { formatNumber } from '@/util/number';
import { formatTime } from '@/util/time';
import CircleIcon from '@mui/icons-material/Circle';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, Table, TableBody, TableCell, TableRow, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import Highlighter from 'react-highlighter-words';
import { useSessionStorage } from 'usehooks-ts';
import SessionFlowMenu, { SessionFlowContextMenu } from './SessionFlowMenu';
import { collapseRow, collapseRowsByName, expandRow, getDuration, getSubroutine } from './sessionFlowUtil';

type SessionFlowProps = {
  sessionData: SessionData;
  lines: SessionTableRow[];
  setLines: (newLines: SessionTableRow[]) => void;
  searchText: string;
  filteredTasks: string[];
  lockedSubroutines: string[];
  setLockedSubroutines: (newSubs: string[]) => void;
  markedLines: number[];
  setMarkedLines: (newLines: number[]) => void;
  selectedRow: number;
  setSelectedRow: (step: number) => void;
  navigateToBuilder: (blockId: string) => void;
};

const SessionFlowTable: FC<SessionFlowProps> = ({
  sessionData,
  lines,
  setLines,
  searchText,
  filteredTasks,
  lockedSubroutines,
  setLockedSubroutines,
  markedLines,
  setMarkedLines,
  selectedRow,
  setSelectedRow,
  navigateToBuilder
}) => {
  const theme = useTheme();

  const [contextMenu, setContextMenu] = useState<SessionFlowContextMenu | null>(null);
  const [highlightChannel, setHighlightChannel] = useSessionStorage<string>('SessionHighlightChannel', '');
  const [highlightSub, setHighlightSub] = useSessionStorage<string>('SessionHighlightSub', '');
  const [highlightTask, setHighlightTask] = useSessionStorage<string>('SessionHighlightTask', '');
  const [highlightBlockId, setHighlightBlockId] = useSessionStorage<string>('SessionHighlightBlockId', '');
  const [highlightBlockType, sethighlightBlockType] = useSessionStorage<string>('SessionhighlightBlockType', '');

  const handleContextMenu = (event: React.MouseEvent, line: SessionTableRow) => {
    event.stopPropagation();
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      line,
      isMarked: markedLines.includes(line.row)
    });
  };

  const getTaskValue = (line: Line) => {
    let result = '';
    const index = sessionData.tasks.findIndex((task) => task.task === line.task);
    for (let i = 0; i < index; i++) {
      const task = sessionData.tasks[i];
      if (filteredTasks.includes(task.task) && line.row > task.first_row && line.row < task.last_row) {
        result += '|  ';
      } else {
        result += '.  ';
      }
    }
    return result;
  };

  const getExpandType = (line: SessionTableRow) => {
    switch (line.expanded) {
      case 2:
        return 'v';
      case 0:
        return ' ';
      case 1:
      default:
        return '>';
    }
  };

  const handleSubroutineLock = (currentSub: Subroutine) => {
    if (lockedSubroutines.includes(currentSub.name)) {
      setLockedSubroutines(lockedSubroutines.filter((subName) => subName !== currentSub.name));
    } else {
      setLines(collapseRowsByName(currentSub.name, lines, sessionData));
      setLockedSubroutines([...lockedSubroutines, currentSub.name]);
    }
  };

  const handleMarkLines = (row: number) => {
    if (markedLines.includes(row)) {
      setMarkedLines(markedLines.filter((line) => line !== row));
    } else {
      const newLines = [...markedLines, row].sort((a, b) => a - b);
      setMarkedLines(newLines);
    }
  };

  const handleHighlightBlockId = (blockId: string, isShortcut?: boolean) => {
    setHighlightBlockId(isShortcut && highlightBlockId === blockId ? '' : blockId);
    sethighlightBlockType('');
    setHighlightTask('');
    setHighlightSub('');
    setHighlightChannel('');
  };

  const handlehighlightBlockType = (blockType: string, isShortcut?: boolean) => {
    sethighlightBlockType(isShortcut && highlightBlockType === blockType ? '' : blockType);
    setHighlightBlockId('');
    setHighlightTask('');
    setHighlightSub('');
    setHighlightChannel('');
  };

  const handleHighlightTask = (task: string, isShortcut?: boolean) => {
    setHighlightTask(isShortcut && highlightTask === task ? '' : task);
    setHighlightSub('');
    setHighlightChannel('');
    setHighlightBlockId('');
    sethighlightBlockType('');
  };

  const handleHighlightSub = (subName: string, isShortcut?: boolean) => {
    setHighlightSub(isShortcut && highlightSub === subName ? '' : subName);
    setHighlightTask('');
    setHighlightChannel('');
    setHighlightBlockId('');
    sethighlightBlockType('');
  };

  const handleHighlightChannel = (channel: string, isShortcut?: boolean) => {
    setHighlightChannel(isShortcut && highlightChannel === channel ? '' : channel);
    setHighlightTask('');
    setHighlightSub('');
    setHighlightBlockId('');
    sethighlightBlockType('');
  };

  const removeHighLight = () => {
    setHighlightChannel('');
    setHighlightTask('');
    setHighlightSub('');
    setHighlightBlockId('');
    sethighlightBlockType('');
  };

  const handleMenuSubmit = (type: string, id: string) => {
    switch (type) {
      case 'marker':
        handleMarkLines(Number(id));
        break;
      case 'block':
        handleHighlightBlockId(id);
        break;
      case 'blocktype':
        handlehighlightBlockType(id);
        break;
      case 'task':
        handleHighlightTask(id);
        break;
      case 'subroutine':
        handleHighlightSub(id);
        break;
      case 'channel':
        handleHighlightChannel(id);
        break;
      case 'navigate':
        navigateToBuilder(id);
        break;
      default:
        removeHighLight();
        break;
    }
  };

  const highlightLine = (line: Line) => {
    if (
      highlightChannel === line.channel ||
      highlightSub === line.sub_name.trim() ||
      highlightTask === line.task ||
      highlightBlockId === line.block_id ||
      highlightBlockType === line.block_type
    )
      return 'cyan';
    return 'unset';
  };

  const bgColor = (row: number) => {
    const marked = 'green';
    const selected = theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[200];

    if (row === selectedRow && markedLines.includes(row))
      return `linear-gradient(90deg, ${marked} 0%, ${selected} 15%, ${selected} 85%, ${marked} 100%)`;
    if (row === selectedRow) return selected;
    if (markedLines.includes(row)) return marked;
    return 'unset';
  };

  return (
    <>
      <Table size='small'>
        <TableBody>
          {lines.map((line) => {
            const currentSub = getSubroutine(line.sub_id, sessionData);
            const isLocked = currentSub && lockedSubroutines.includes(currentSub?.name);
            return (
              <TableRow
                key={`${line.row}`}
                data-row={line.row}
                tabIndex={0}
                onContextMenu={(event) => handleContextMenu(event, line)}
                sx={{
                  background: bgColor(line.row),
                  '& *': { color: highlightLine(line) },
                  '&:hover': { opacity: '60%' }
                }}
                onClick={() => setSelectedRow(line.row)}
              >
                <TableCell
                  className='session-step'
                  width={1}
                  onClick={(e) => {
                    e.ctrlKey && e.stopPropagation();
                    e.ctrlKey && handleMarkLines(line.row);
                  }}
                >
                  <Box textAlign='right'>
                    <Highlighter
                      searchWords={[searchText]}
                      highlightStyle={{ backgroundColor: theme.palette.grey[400] }}
                      autoEscape
                      textToHighlight={line.step.replace('E', '')}
                    />
                  </Box>
                </TableCell>
                <TableCell className='session-timestamp' width={1}>
                  {formatTime(line.timestamp)}
                </TableCell>
                <TableCell className='session-time-from-start' width={100} sx={{ textAlign: 'right' }}>
                  {formatNumber(dayjs(line.timestamp).diff(dayjs(lines[0].timestamp)))} ms
                </TableCell>
                <TableCell
                  className='session-channel'
                  width={100}
                  sx={{ textAlign: 'right' }}
                  onClick={(e) => {
                    e.ctrlKey && e.stopPropagation();
                    e.ctrlKey && handleHighlightChannel(line.channel, true);
                  }}
                >
                  {line.channel}
                </TableCell>
                <TableCell
                  className='session-task'
                  width={1}
                  sx={{ whiteSpace: 'pre' }}
                  onClick={(e) => {
                    e.ctrlKey && e.stopPropagation();
                    e.ctrlKey && handleHighlightTask(line.task, true);
                  }}
                >
                  {getTaskValue(line)}
                  <CircleIcon sx={{ height: '6px', width: '6px', ml: '-1px' }} />
                </TableCell>
                <TableCell
                  className='session-expanded'
                  width={1}
                  sx={{ cursor: 'pointer', opacity: isLocked ? '30%' : '100%' }}
                  onClick={(e) => {
                    if (line.expanded === 0) return;
                    e.stopPropagation();
                    line.expanded === 1
                      ? setLines(expandRow(line, e.ctrlKey, lines, lockedSubroutines, sessionData))
                      : setLines(collapseRow(line.row, currentSub, lines));
                  }}
                >
                  {getExpandType(line)}
                </TableCell>
                <TableCell
                  className='session-subroutine'
                  onClick={(e) => {
                    e.ctrlKey && e.stopPropagation();
                    e.ctrlKey && handleHighlightSub(line.sub_name.trim(), true);
                  }}
                  sx={{ whiteSpace: 'pre', '&:hover .lock-icon': { visibility: 'visible' } }}
                >
                  <Box>
                    <span>{line.sub_name}</span>
                    {currentSub?.first_row === line.row && (
                      <Box
                        display='inline-flex'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubroutineLock(currentSub);
                        }}
                      >
                        {lockedSubroutines.includes(line.sub_name.trim()) ? (
                          <LockIcon sx={{ width: 14, height: 14, cursor: 'pointer', ml: 1 }} />
                        ) : (
                          <LockOpenIcon
                            className='lock-icon'
                            sx={{ width: 14, height: 14, visibility: 'hidden', cursor: 'pointer', ml: 1 }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell className='session-block'>
                  <Box
                    display='grid'
                    gridTemplateColumns='auto minmax(200px, max-content)'
                    alignItems='center'
                    justifyContent='end'
                    gap={1}
                  >
                    <Box
                      textAlign='right'
                      whiteSpace='nowrap'
                      onClick={(e) => {
                        if (!e.ctrlKey) return;

                        e.stopPropagation();
                        handleHighlightBlockId(line.block_id, true);
                      }}
                    >
                      <Highlighter
                        searchWords={[searchText]}
                        highlightStyle={{ backgroundColor: theme.palette.grey[400] }}
                        autoEscape
                        textToHighlight={line.block_id.toString()}
                      />
                    </Box>

                    <Box
                      onClick={(e) => {
                        if (!e.ctrlKey) return;

                        e.stopPropagation();
                        handlehighlightBlockType(line.block_type, true);
                      }}
                    >
                      <Highlighter
                        searchWords={[searchText]}
                        highlightStyle={{ backgroundColor: theme.palette.grey[400] }}
                        autoEscape
                        textToHighlight={line.block_type}
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className='session-duration' width={100} sx={{ textAlign: 'right' }}>
                  {getDuration(line, sessionData)} ms
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <SessionFlowMenu
        contextMenu={contextMenu}
        highlights={{
          block: highlightBlockId,
          blocktype: highlightBlockType,
          task: highlightTask,
          subroutine: highlightSub,
          channel: highlightChannel
        }}
        onSubmit={handleMenuSubmit}
        onClose={() => setContextMenu(null)}
      />
    </>
  );
};

export default SessionFlowTable;
